import { useEffect, useState } from "react";
import { renderToString } from "react-dom/server";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import StopList from "./StopList";
import FigureTile from "./FigureTile";
import MapOverlay from "../Map/MapOverlay";
import MapPopup from "./MapPopup";
import MapLayout from "../Map/MapLayout";
import MapOverlayList from "../Map/MapOverlayList";
import Timeline from "../Timeline";
import MapControlToggle from "../Map/MapControlToggle";

const Map = dynamic(() => import("../Map/Map"), { ssr: false });

export default function FigurePageDetail({
  name,
  stops,
  portrait,
  centers,
  memorials,
  labels,
  locale,
}) {
  const router = useRouter();
  const [mapControlState, setMapControlState] = useState("centers");
  const [selectedStop, setSelectedStop] = useState();
  const [selectedTime, setSelectedTime] = useState();
  const [stopList, setStopList] = useState(stops);
  const [journeyMarkers, setJourneyMarkers] = useState([]);
  const [placeOfHomeMarkers, setPlaceOfHomeMarkers] = useState([]);
  const [additionalMarkers, setAdditionalMarkers] = useState([]);
  const [journeyLines, setJourneyLines] = useState([]);
  const [centersData, setCentersData] = useState(centers);

  /**
   * 1. only return all locations that are place of home
   * 2. filter all null values
   * 3. filter all duplicates
   */
  const placeOfHomeLocations = stops
    .map((stop) => {
      if (stop.stop_type.value === "place_of_home") return stop.location;
      else return null;
    })
    .filter((location) => location)
    .filter(
      (location, index, locations) =>
        locations.findIndex((l) => l.id === location.id) === index,
    );

  const drawJourney = () => {
    if (selectedStop === undefined || selectedStop < 0) return;
    if (!stops || stops.length < 1) return;

    const isPlaceOfHome = stops[selectedStop].stop_type.value === "place_of_home";
    let prevPlaceOfHomeIndex;
    let nextPlaceOfHomeIndex;

    nextPlaceOfHomeIndex = findNearPlaceOfHome(selectedStop, true);

    /**
     * if next poh ist not found it means that it is
     * somwhere near the end of the list and we just
     * take the last stop of the list as "final destination"
     */
    if (!nextPlaceOfHomeIndex) {
      nextPlaceOfHomeIndex = stops.length - 1;
    }

    if (isPlaceOfHome) {
      prevPlaceOfHomeIndex = selectedStop;
    } else {
      prevPlaceOfHomeIndex = findNearPlaceOfHome(selectedStop, false);
    }

    updateJourneyMarkersAndLine(prevPlaceOfHomeIndex, nextPlaceOfHomeIndex);

    const newStopList = stops.map((stop) => {
      const { index } = stop;

      if (index >= prevPlaceOfHomeIndex && index <= nextPlaceOfHomeIndex) {
        return {
          ...stops[index],
          highlight: true,
        };
      } else {
        return {
          ...stops[index],
          highlight: false,
        };
      }
    });

    setStopList(newStopList);
  };

  const findNearPlaceOfHome = (index, forward = true) => {
    if (forward) {
      for (let i = index + 1; i < stops.length; i++) {
        if (stops[i].stop_type.value === "place_of_home") return i;
      }
    } else {
      for (let i = index - 1; i >= 0; i--) {
        if (stops[i].stop_type.value === "place_of_home") return i;
      }
    }
  };

  const updateJourneyMarkersAndLine = (start, end) => {
    const markers = [];
    const lines = [];
    let lastLinePoint;

    stops.slice(start, end + 1).forEach((stop, index) => {
      if (!stop.location) return;

      const stopSelected = stop.index === selectedStop;
      const stopAlreadyTraveled = selectedStop >= start + index;

      const marker = {
        title: stop.location.title,
        latitude: stop.location.latitude || "0",
        longitude: stop.location.longitude || "0",
        icon: stop.stop_type.value === "place_of_home" ? "house" : "circle",
        color: stopAlreadyTraveled ? stop.color : undefined,
        zIndexOffset: stopSelected ? 100 : stopAlreadyTraveled ? 90 : 0,
        popupContent: stopSelected
          ? renderToString(<MapPopup stop={stop} locale={locale} />)
          : null,
      };
      const linePoint = [stop.location.latitude || "0", stop.location.longitude || "0"];

      markers.push(marker);

      if (lastLinePoint) {
        const className = stopAlreadyTraveled ? "polyline active" : "polyline";
        lines.push({ className, coords: [lastLinePoint, linePoint] });
      }
      lastLinePoint = linePoint;
    });

    setJourneyMarkers(markers);
    setJourneyLines(lines);
  };

  const handleMapControl = (option) => {
    if (option === 0) setMapControlState("centers");
    if (option === 1) setMapControlState("places_of_home");
  };

  useEffect(drawJourney, [selectedStop, locale]);

  useEffect(() => {
    const selectedTimeDate = new Date(selectedTime);

    const stopToSelect = stopList.find((stop) => {
      const startCalculated = new Date(stop.start_earliest);
      const endCalculated = new Date(stop.end_latest);

      return selectedTimeDate >= startCalculated && selectedTimeDate <= endCalculated;
    });

    const index = stopToSelect ? stopToSelect.index : null;

    if (Number.isInteger(index)) {
      const url = {
        pathname: router.pathname,
        query: { ...router.query, stop: index },
      };
      router.replace(url, url, { shallow: true });
    }
  }, [selectedTime]);

  useEffect(() => {
    let stop = parseInt(router.query.stop);
    stop = isNaN(stop) ? undefined : stop;
    const time = router.query.time;

    if (time && time !== selectedTime) {
      setSelectedTime(time);
      return;
    }

    if (Number.isInteger(stop) && stop !== selectedStop) {
      setSelectedStop(stop);
    }
  }, [router.query]);

  useEffect(() => {
    if (mapControlState === "centers") setCentersData(centers);
    else setCentersData([]);

    if (mapControlState === "places_of_home") {
      setPlaceOfHomeMarkers(
        placeOfHomeLocations.map((location) => ({
          title: location.title,
          latitude: location.latitude || "0",
          longitude: location.longitude || "0",
          icon: "house",
          href: `/locations/${location.slug}`,
        })),
      );
    } else {
      setPlaceOfHomeMarkers([]);
    }
  }, [mapControlState, locale]);

  useEffect(() => {
    setAdditionalMarkers(placeOfHomeMarkers.concat(memorials));
  }, [placeOfHomeMarkers, memorials]);

  return (
    <MapLayout>
      <Map
        markers={journeyMarkers}
        additionalMarkers={additionalMarkers}
        polylines={journeyLines}
        centers={centersData}
        paddingBottomRight={[390, 0]}
        paddingTopLeft={[350, 250]}
      />
      <MapOverlay className="left-0 flex-col">
        <MapControlToggle
          options={[labels["show-centers"], labels["show-places-of-home"]]}
          onChange={handleMapControl}
        />
        <FigureTile
          name={name}
          portrait={portrait}
          className="ml-3 mt-3 w-20 xl:w-40 text-gamma"
        />
      </MapOverlay>
      <MapOverlay className="right-0 h-full pt-3">
        <MapOverlayList>
          <StopList stops={stopList} />
        </MapOverlayList>
      </MapOverlay>
      <Timeline stops={stops} specification={"figure"} />
    </MapLayout>
  );
}
